/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Tooltip } from '@mui/material'
import {
  primaryColors,
  transparentColors
} from '@assets/styles/helpers/colors.styles'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButton from '@/shared/components/IconButton/IconButton'

import {
  imageUploadButtonStyles,
  popperStyles,
  tooltipStyles
} from './ImageUploadButton.styles'
import styles from './ImageUploadButton.module.scss'
import OptionsTile from '../OptionsTile/OptionsTile'
import ConfirmationModal from '../Modal/ConfirmationModal/ConfirmationModal'
import { useCropImageStore } from '@/shared/store/useCropImageStore'
import { useImageSlots } from '@/shared/store/useImageSlots'

interface ImageUploadButtonProps {
  imageShape: 'circle' | 'rectangle'
  label: string
  order?: number
  imageUrl?: string
  isVertical?: boolean
  slotId: string
  /**
   * Runs after the picked image is dropped from the slot, once the user has
   * confirmed. For an owner that also keeps the picture somewhere else, on the
   * server, or in its own store, this is where that copy goes. Without it the
   * trash only forgets what was just cropped.
   */
  onDelete?: () => void
}

interface ImageUploadButtonModalProps {
  imageSize: { width: number; height: number }
  stencilSize: { width: number; height: number }
}

function ImageUploadButton(
  props: ImageUploadButtonProps & ImageUploadButtonModalProps
) {
  const {
    imageShape,
    label,
    order,
    imageUrl,
    isVertical,
    imageSize,
    stencilSize,
    slotId,
    onDelete
  } = props

  const { t } = useTranslation()
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const toggleTooltip = () => setIsTooltipOpen(!isTooltipOpen)

  const slot = useImageSlots(s => s.slots[slotId])
  const clearSlot = useImageSlots(s => s.clear)

  const preview = useMemo(
    () => slot?.croppedUrl ?? slot?.originalUrl ?? imageUrl,
    [slot?.croppedUrl, slot?.originalUrl, imageUrl]
  )

  const openCropImageModal = useCropImageStore(state => state.open)

  const handleOnclick = () => {
    if (!preview) {
      openCropImageModal({
        slotId,
        imageUrl,
        imageShape,
        imageSize,
        stencilSize
      })
    }
  }

  const handleOnEditclick = () => {
    if (preview) {
      openCropImageModal({
        slotId,
        imageUrl: preview,
        imageShape,
        imageSize,
        stencilSize
      })
    }
  }

  const handleOnDeleteClick = () => {
    setIsTooltipOpen(false)
    setIsDeleteConfirmationOpen(true)
  }

  const handleConfirmDelete = () => {
    clearSlot(slotId)
    onDelete?.()
    setIsDeleteConfirmationOpen(false)
  }

  const handleCloseTooltip = () => {
    if (isTooltipOpen) {
      setIsTooltipOpen(false)
    }
  }

  return (
    <div key={slotId} className={styles.imageUploadButtonWrapper}>
      <Tooltip
        title={
          <OptionsTile
            onEditClick={handleOnEditclick}
            onDeleteClick={handleOnDeleteClick}
            onClose={handleCloseTooltip}
          />
        }
        slotProps={{
          tooltip: {
            sx: { ...tooltipStyles }
          },
          popper: {
            sx: { ...popperStyles, zIndex: 5 }
          }
        }}
        placement="bottom-end"
        open={isTooltipOpen}
      >
        <Box
          onClick={handleOnclick}
          sx={{
            ...imageUploadButtonStyles,
            cursor: preview ? 'default' : 'pointer',
            '&:hover': {
              background: preview
                ? transparentColors.whiteShade2
                : transparentColors.whiteShade3
            },
            '&:active': {
              background: preview
                ? transparentColors.whiteShade2
                : transparentColors.whiteShade4
            }
          }}
        >
          <div
            className={`${styles.imagePreview} ${
              imageShape === 'circle' ? styles.circle : styles.rectangle
            } ${isVertical ? styles.vertical : ''}`}
            style={{
              backgroundImage: preview ? `url(${preview})` : 'none',
              backgroundColor: preview
                ? primaryColors.black
                : transparentColors.transparent,
              border: preview
                ? `2px solid ${primaryColors.white}`
                : `2px dashed ${primaryColors.white}`
            }}
          >
            {!preview && (
              <div className={styles.imageUploadButtonPlaceholderContainer}>
                <i
                  className={`las la-image icon-big ${styles.imageUploadButtonPlaceholder}`}
                />
                <i
                  className={`las la-plus icon-small ${styles.imageUploadButtonPlaceholder}`}
                />
              </div>
            )}
          </div>
          <div className={styles.imageUploadButtonDescriptionAndOptions}>
            <p className={`body-bold-small ${styles.imageUploadButtonLabel}`}>
              {`${label} ${order ? `No.${order}` : ''}`}
            </p>

            {preview ? (
              <IconButton
                icon="las la-sliders-h icon-medium"
                color="primary"
                onClick={toggleTooltip}
              />
            ) : null}
          </div>
        </Box>
      </Tooltip>
      <ConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        title={t('IMAGE_UPLOAD.DELETE_CONFIRMATION.TITLE')}
        message={t('IMAGE_UPLOAD.DELETE_CONFIRMATION.DESCRIPTION')}
        onConfirm={handleConfirmDelete}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        disablePortal={false}
        resetsFormDirty={false}
      />
    </div>
  )
}

export default ImageUploadButton
